import React, { useContext } from 'react';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { Button, Label } from 'edozvola-frontend-lib/dist/ui';
import { Alert, Col, FormGroup, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import {
  fetchCaseConstructionLegalityQuery,
  saveCaseConstructionLegalityQuery,
} from 'app/edozvola/cases/case-construction-legality/service/case-construction-legality.queries';
import { Loader } from 'app/shared/loader/loader';
import CaseConstructionLegalityDocuments from 'app/edozvola/cases/case-construction-legality/documents/case-construction-legality-documents';
import { fetchExistingBuildingRatioCodebookQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { CaseTabContext } from '../case-form-wrapper';
import {caseConstructionLegalityInitial, CaseConstructionLegalityModel} from './model/case-construction-legality.model';
import { constructionLegalityFormValidationSchema } from './model/case-construction-legality.schema';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { ToggleWithLabels } from 'edozvola-frontend-lib/dist/ui/components/toggle-with-labels';
import { getCaseContext } from 'app/shared/util/context-util';

const CaseConstructionLegalityForm = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const { data: initialData, isLoading: initialDataLoading } = fetchCaseConstructionLegalityQuery(id);
  const { data: existingBuildingRatioOptions, isLoading: existingBuildingRatioOptionsLoading } = fetchExistingBuildingRatioCodebookQuery();
  const { mutate: saveCaseConstructionLegalityMutation, isPending: isCaseConstructionLegalitySaving } =
    saveCaseConstructionLegalityQuery(id);

  const { sidebarItemPair } = getCaseContext();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialData as CaseConstructionLegalityModel || caseConstructionLegalityInitial,
    validationSchema: constructionLegalityFormValidationSchema,
    validateOnMount: true,
    onSubmit: values => saveCaseConstructionLegalityMutation(values),
  });

  const plotConstructionAndProofOfLegalityChanged = (event: any) => {
    formik.setValues({
      ...formik.values,
      plotConstructionAndProofOfLegality: event.target.checked,
      existingBuildingRatio: null,
    });
  };

  return (
    <>
      <FormikProvider value={formik}>
        <Form>
          <FormikValueObserver
            onChange={dirty => {
              if (dirty.dirty) {
                dispatch(setFormChanged(true));
              } else {
                dispatch(initCaseFormChange());
              }
            }}
          />
          {initialDataLoading && <Loader />}
          {!initialDataLoading && (
            <div>
              <Row>
                <Col md={4}>
                  <Label labelText={'Izgrađenost čestice građevinama te dokaz legalnosti'} />
                  <ToggleWithLabels
                    trueLabel="DA"
                    falseLabel="NE"
                    checked={formik.values?.plotConstructionAndProofOfLegality}
                    disabled={sidebarItemPair?.readOnly}
                    onChange={plotConstructionAndProofOfLegalityChanged}
                  />
                </Col>
              </Row>
              {formik.dirty && !initialData?.plotConstructionAndProofOfLegality && formik.values.plotConstructionAndProofOfLegality && (
                <Row>
                  <Col md={5}>
                    <Alert color="warning">Potrebno je prvo spremiti obvezne podatke na kartici da biste mogli dodati akte!</Alert>
                  </Col>
                </Row>
              )}
              <hr />
              <h6 className={'form-header'}>
                Dokaz legalnosti postojećih građevina na građevnoj čestici / obuhvata zahvata u prostoru
              </h6>
              <Row>
                <Col xs={5}>
                  <FormGroup>
                    <Label labelText={'Dokaz legalnosti'} />
                    <Field
                      id="existingBuildingRatio"
                      name="existingBuildingRatio"
                      render={({ field }) => (
                        <PaginatedDropdown
                          onItemSelect={selectedOption => formik.setFieldValue('existingBuildingRatio', selectedOption)}
                          selected={field?.value ?? []}
                          labelKey={'value'}
                          options={existingBuildingRatioOptionsLoading ? [] : existingBuildingRatioOptions}
                          disabled={!formik.values.plotConstructionAndProofOfLegality || sidebarItemPair?.readOnly}
                          placeholder={formik.values.plotConstructionAndProofOfLegality && !sidebarItemPair?.readOnly && 'Odaberi dokaz legalnosti'}
                          error={!sidebarItemPair?.readOnly && (formik.errors.existingBuildingRatio as string)}
                          showErrorMessage
                        />
                      )}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <CaseConstructionLegalityDocuments
                addDocumentDisabled={
                  (formik.dirty && !initialData.plotConstructionAndProofOfLegality && formik.values.plotConstructionAndProofOfLegality) ||
                  !formik.values.plotConstructionAndProofOfLegality
                }
              />

              {!sidebarItemPair?.readOnly && (
                <div className="case-save-button">
                  <Button
                    color={'info'}
                    label={'Spremi'}
                    isLoading={isCaseConstructionLegalitySaving}
                    size={'lg'}
                    onClick={formik.submitForm}
                    disabled={!formik.dirty || !formik.isValid}
                  />
                </div>
              )}
            </div>
          )}
        </Form>
      </FormikProvider>
    </>
  );
};

export default CaseConstructionLegalityForm;
